/**
 * P17-UAT-PRODUCTION-UI-02 · Process-family navigation shells.
 *
 * UI / NAVIGATION ONLY. These are thin page shells (heading + tabs) that MOUNT
 * the existing, independently-sealed Production Entry engines. They NEVER merge
 * transaction state or posting logic — each engine keeps its own rules, number
 * series, stock posting, genealogy, idempotency and reversal.
 *
 * Tab isolation: a `key` tied to the active tab forces a full React remount of
 * the mounted engine on every tab switch, so selected WO / CWO / product /
 * batch / quantity / draft / posting state can NEVER leak between tabs.
 */
import { useState, useEffect } from "react";
import { useSearchParams, Navigate } from "react-router-dom";
import { FactoryIcon, PlusIcon } from "@phosphor-icons/react";
import DepartmentProductionEntry from "@/pages/DepartmentProductionEntry";
import DyeingProductionEntry from "@/pages/DyeingProductionEntry";
import FinishingProductionEntryRegister from "@/pages/FinishingProductionEntryRegister";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function ShellHeader({ overline, title, testId }) {
  return (
    <div data-testid={testId}>
      <div className="overline mb-1 flex items-center gap-2"><FactoryIcon size={11} weight="bold" />{overline}</div>
      <h1 className="font-display text-3xl font-black tracking-tight text-white">{title}</h1>
    </div>
  );
}

function Tabs({ tabs, active, onPick, testId }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testId}>
      {tabs.map(t => {
        const on = t.key === active;
        return (
          <button key={t.key} data-testid={`${testId}-${t.key}`} onClick={() => onPick(t.key)}
            className={`px-5 py-2.5 rounded-sm border text-sm font-bold tracking-tight transition-colors ${
              on ? "bg-[var(--bg-2)] border-2 text-white" : "bg-[var(--bg-1)]/60 border-[var(--border)] text-zinc-300 hover:border-zinc-500"}`}
            style={on ? { borderColor: t.color } : {}}>
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Covering & Spandex Warping ─────────────────────────────────────────────
const CS_TABS = [
  { key: "covering", label: "Covering", dept: "COVERING", color: "#f472b6" },
  { key: "spandex", label: "Spandex Warping", dept: "SPANDEX_WARPING", color: "#fbbf24" },
];

export function CoveringSpandexEntry() {
  const [sp, setSp] = useSearchParams();
  const active = CS_TABS.find(t => t.key === sp.get("tab")) ? sp.get("tab") : "covering";
  const tab = CS_TABS.find(t => t.key === active);
  return (
    <div className="p-6 space-y-5" data-testid="covering-spandex-shell">
      <ShellHeader overline="Production Entry · Covering & Spandex Warping" title="Covering & Spandex Warping" testId="cs-header" />
      <Tabs tabs={CS_TABS} active={active} onPick={(k) => setSp({ tab: k })} testId="cs-tabs" />
      <DepartmentProductionEntry key={tab.key} lockedDept={tab.dept} />
    </div>
  );
}

// ── Finishing & Dyeing ─────────────────────────────────────────────────────
// P17-UAT-18A-3 · Dyeing tab mounts the dedicated controlled Dyeing engine
// (DyeingProductionEntry · register-first + "+ Record Dyeing"), backed by the
// backend dyeing_run V2 material contract. Finishing stays the sealed engine.
const FD_TABS = [
  { key: "finishing", label: "Finishing", dept: "FINISHING", color: "#a78bfa" },
  { key: "dyeing", label: "Dyeing", dept: null, color: "#38bdf8" },
];

export function FinishingDyeingEntry() {
  const [sp, setSp] = useSearchParams();
  const active = FD_TABS.find(t => t.key === sp.get("tab")) ? sp.get("tab") : "finishing";
  const finishingPe = sp.get("finishingPe");
  return (
    <div className="p-6 space-y-5" data-testid="finishing-dyeing-shell">
      <ShellHeader overline="Production Entry · Finishing & Dyeing" title="Finishing & Dyeing" testId="fd-header" />
      <Tabs tabs={FD_TABS} active={active} onPick={(k) => setSp(k === "finishing" && finishingPe ? { tab: k, finishingPe } : { tab: k })} testId="fd-tabs" />
      {active === "dyeing"
        ? <div data-testid="fd-dyeing-mount"><DyeingProductionEntry key="dyeing" /></div>
        : <DepartmentProductionEntry key="finishing" lockedDept="FINISHING" />}
    </div>
  );
}

// ── PMS.V3 REV-1 · clean departmental Production Entry screens ──────────────
// UI/NAVIGATION ONLY. Each screen mounts the SAME existing, sealed engine the
// combined Finishing & Dyeing shell used — no new posting/reversal/stock logic.
// Finishing  → DepartmentProductionEntry(lockedDept="FINISHING")  (unchanged engine)
// Dyeing     → DyeingProductionEntry                              (unchanged engine)
// REV-1B (Jun 2026) · Finishing aligned to the proven Dyeing UX: register-first
// with a "+ Record Finishing" dialog that mounts the EXISTING Finishing engine
// (DepartmentProductionEntry · FINISHING). Register reused as-is
// (FinishingProductionEntryRegister). No engine/API/posting/reversal change.
export function FinishingEntry() {
  const [sp, setSp] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [registerKey, setRegisterKey] = useState(0);
  const [engineKey, setEngineKey] = useState(0);

  // ?finishingPe= continuation (preserved from REV-1): auto-open the Record
  // dialog so the existing engine loads/continues that saved draft.
  useEffect(() => {
    if (sp.get("finishingPe")) setOpen(true);
  }, [sp]);

  const openNew = () => {
    if (sp.get("finishingPe")) setSp({});     // drop any stale continuation param
    setEngineKey((k) => k + 1);               // fresh, blank engine mount
    setOpen(true);
  };

  const onOpenChange = (next) => {
    setOpen(next);
    if (!next) {
      if (sp.get("finishingPe")) setSp({});   // clear continuation on close
      setRegisterKey((k) => k + 1);           // refresh register (show new/posted entry)
    }
  };

  return (
    <div className="p-6 space-y-5" data-testid="finishing-entry-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <ShellHeader overline="Production Entry · Finishing" title="Finishing Production" testId="finishing-entry-header" />
        <button onClick={openNew} data-testid="finishing-record-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-sm bg-emerald-500 text-zinc-950 font-bold text-sm hover:bg-emerald-400 transition-colors">
          <PlusIcon size={15} weight="bold" /> Record Finishing
        </button>
      </div>

      {/* Existing, authoritative Finishing register — reused as-is. */}
      <FinishingProductionEntryRegister key={registerKey} />

      {/* Record Finishing — existing Finishing engine mounted in a dialog. */}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-5xl w-[96vw] max-h-[90vh] overflow-y-auto" data-testid="finishing-record-dialog">
          <DialogHeader>
            <DialogTitle>Record Finishing Production</DialogTitle>
          </DialogHeader>
          <DepartmentProductionEntry key={`fin-${engineKey}`} lockedDept="FINISHING" />
          {/* REV-1B.1 · explicit Cancel — UI navigation only. Reuses the same
              safe dialog-close path (onOpenChange(false)): discards the unsaved
              form, returns to the register, calls NO Save/Post/reversal API.
              Save / Save & Post remain inside the frozen engine above. */}
          <div className="mt-4 flex justify-start border-t border-[var(--border)] pt-4">
            <button type="button" data-testid="finishing-cancel-btn" onClick={() => onOpenChange(false)}
              className="px-5 py-2.5 rounded-sm border border-[var(--border)] text-zinc-300 text-sm font-semibold hover:bg-[var(--bg-2)] transition-colors">
              Cancel
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function DyeingEntry() {
  return (
    <div className="p-6 space-y-5" data-testid="dyeing-entry-page">
      <ShellHeader overline="Production Entry · Dyeing" title="Dyeing Production Entry" testId="dyeing-entry-header" />
      <DyeingProductionEntry key="dyeing" />
    </div>
  );
}

// Legacy combined route → safe redirect to the clean departmental screens.
// Preserves ?tab=dyeing and the Finishing draft-continuation (?finishingPe=)
// deep links so existing bookmarks never land on a broken page.
export function FinishingDyeingLegacyRedirect() {
  const [sp] = useSearchParams();
  if (sp.get("tab") === "dyeing") return <Navigate to="/production/dyeing-entry" replace />;
  const fp = sp.get("finishingPe");
  return <Navigate to={fp ? `/production/finishing-entry?finishingPe=${encodeURIComponent(fp)}` : "/production/finishing-entry"} replace />;
}

// ── Legacy /production/department-entry compatibility redirect ──────────────
// One operational entry path per process: the generic tabbed screen is no longer
// a live editable route. Preserve Finishing draft-continuation deep links.
export function DepartmentEntryLegacyRedirect() {
  const [sp] = useSearchParams();
  const fp = sp.get("finishingPe");
  if (fp) return <Navigate to={`/production/finishing-dyeing-entry?tab=finishing&finishingPe=${encodeURIComponent(fp)}`} replace />;
  return <Navigate to="/production/warping-entry" replace />;
}
